<template>
  <!--
    Lightweight hand-rolled carousel.

    Why not v-carousel? It defers the LCP image's paint by ~2.5s
    while Vuetify's carousel/img components mount and hydrate.
    Plain <img> elements render the moment the bytes arrive, which
    is immediate for slide 1 because index.html preloads it.
  -->
  <div
    class="hero-carousel"
    role="region"
    aria-label="Hero image carousel"
    aria-roledescription="carousel"
  >
    <img
      v-for="(slide, i) in slides"
      :key="i"
      :src="slide.src"
      :alt="slide.alt"
      :class="['hero-slide', { 'hero-slide--active': i === activeIndex }]"
      :loading="i === 0 ? 'eager' : 'lazy'"
      :fetchpriority="i === 0 ? 'high' : 'low'"
      width="1600"
      height="1067"
      decoding="async"
      :aria-hidden="i === activeIndex ? undefined : 'true'"
    />

    <!-- Dot indicators -->
    <div class="hero-dots" aria-label="Carousel slide navigation">
      <button
        v-for="(slide, i) in slides"
        :key="i"
        type="button"
        class="hero-dot"
        :class="{ 'hero-dot--active': i === activeIndex }"
        :aria-label="`Show slide ${i + 1}: ${slide.alt}`"
        :aria-current="i === activeIndex ? 'true' : undefined"
        @click="goTo(i)"
      ></button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";

const slides = [
  { src: "/carousel/1.webp", alt: "Natural herbs and botanicals used in evidence-based medicine" },
  { src: "/carousel/2.webp", alt: "Fresh fruits, vegetables, and whole foods supporting nutritional health" },
  { src: "/carousel/3.webp", alt: "Vitamins, minerals, and nutritional supplements for natural wellness" },
  { src: "/carousel/4.webp", alt: "Traditional plant-based remedies grounded in scientific research" },
  { src: "/carousel/5.webp", alt: "Holistic nutrition and natural medicine for whole-body wellness" },
];

const activeIndex = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;

const startCycle = () => {
  stopCycle();
  timer = setInterval(() => {
    activeIndex.value = (activeIndex.value + 1) % slides.length;
  }, 3000);
};

const stopCycle = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};

const goTo = (i: number) => {
  activeIndex.value = i;
  startCycle(); // restart timer so user input doesn't immediately advance
};

onMounted(startCycle);
onUnmounted(stopCycle);
</script>

<style scoped>
.hero-carousel {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.hero-slide {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.6s ease;
  will-change: opacity;
}

.hero-slide--active {
  opacity: 1;
}

.hero-dots {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 1;
}

.hero-dot {
  width: 10px;
  height: 10px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.hero-dot:hover {
  background-color: rgba(255, 255, 255, 0.85);
}

.hero-dot--active {
  background-color: #ffffff;
  transform: scale(1.25);
}

.hero-dot:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .hero-slide {
    transition: none;
  }
}
</style>
